// Editor edvid — tela cheia (portal no body, como o VideoProjectEditor), abas
// "1 · Corte" → "Estilo" → "2 · Final" com a estrutura do preview do edvid e a
// marca IntegraIA. Estilo abre quando existe corte; Final, quando existe visual.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import type { EdlRange, EdvidJobState, StyleSetup } from '../../../types/videoEdit';
import { apiEdvidClient, useEdvidJob, type EdvidClient } from './useEdvidJob';
import { useEdvidNotes, NoteEditor } from './notes';
import { CutTab } from './CutTab';
import { StyleTab } from './StyleTab';
import { FinalTab, type VisualSaveResult } from './FinalTab';
import type { VisualProps } from './demoProps';
import { HelpModal } from './HelpModal';
import { BusyOverlay } from './ui';
import { IconBack } from './icons';
import { PublishButton } from './EdvidPublish';

export type EdvidTab = 'cut' | 'style' | 'final';

const PHASE_LABEL: Record<EdvidJobState['phase'], string> = {
  cutting: 'Cortando…',
  cut_ready: 'Corte pronto',
  visual_rendering: 'Gerando visual…',
  done: 'Final pronto',
  failed: 'Falhou',
};

function defaultTab(e: EdvidJobState | null): EdvidTab {
  if (!e?.cut) return 'cut';
  if (e.visual) return 'final';
  if (e.phase === 'visual_rendering' || e.cut.approvedAt) return 'style';
  return 'cut';
}

const isConflict = (err: any) => err?.status === 409 || /conflito|revis[aã]o|recarregue|outra aba/i.test(err?.message || '');

export function EdvidEditor({ jobId, title, onClose, client = apiEdvidClient, initialTab, brandAccent }: {
  jobId: string;
  title?: string;
  onClose: () => void;
  client?: EdvidClient;
  initialTab?: EdvidTab;
  brandAccent?: string | null;
}) {
  const { job, edvid, loading, error, reload, accept } = useEdvidJob(jobId, client);
  const [tab, setTab] = useState<EdvidTab | null>(initialTab || null);
  const [help, setHelp] = useState(false);
  const [cutDirty, setCutDirty] = useState(0);
  const [finalDirty, setFinalDirty] = useState(0);
  const [styleSubmitting, setStyleSubmitting] = useState(false);
  const [applyingNotes, setApplyingNotes] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const awaitingVisual = useRef(false);
  const notes = useEdvidNotes(edvid?.notes);

  // aba inicial conforme a fase, quando o job chega
  useEffect(() => { if (edvid && tab == null) setTab(defaultTab(edvid)); }, [edvid, tab]);
  // "Gerar visual" terminou → vai para o Final; falhou → diz por quê
  const sawRendering = useRef(false);
  useEffect(() => {
    if (!awaitingVisual.current) return;
    if (edvid?.phase === 'visual_rendering') { sawRendering.current = true; return; }
    if (!sawRendering.current) return;
    awaitingVisual.current = false;
    sawRendering.current = false;
    if (edvid?.visual && !edvid.error) {
      setTab('final');
      toast.success('Visual pronto.');
    } else {
      toast.error(edvid?.error || 'O visual não pôde ser gerado. Tente de novo.', { duration: 8000 });
    }
  }, [edvid?.visual, edvid?.phase, edvid?.error]);

  const flash = useCallback((msg: string) => { toast(msg, { duration: 2600 }); }, []);
  const hasCut = !!edvid?.cut;
  const hasVisual = !!edvid?.visual;
  const activeTab: EdvidTab = tab === 'final' && !hasVisual ? (hasCut ? 'style' : 'cut') : tab === 'style' && !hasCut ? 'cut' : (tab || 'cut');
  const unsaved = cutDirty + finalDirty + notes.local.filter(n => n.text.trim()).length;

  const saveCut = useCallback(async (ranges: EdlRange[], baseRevision: number) => {
    setSaveState('saving');
    try {
      await accept(await client.saveCut(jobId, ranges, baseRevision));
      setSaveState('saved');
      toast.success('Ajustes enviados — re-renderizando o corte.');
      return true;
    } catch (e: any) {
      setSaveState('error');
      if (isConflict(e)) {
        await reload();
        toast.error('O corte mudou em outro lugar. Seus ajustes continuam aqui: salve de novo para aplicá-los sobre a revisão atual, ou descarte para ver a nova.', { duration: 6000 });
      } else toast.error(e?.message || 'Erro ao salvar o corte.');
      return false;
    }
  }, [accept, client, jobId, reload]);

  const recut = useCallback(async (note?: string) => {
    try {
      await accept(await client.recut(jobId, note));
      toast.success('A IA está refazendo o corte.');
    } catch (e: any) { toast.error(e?.message || 'Erro ao pedir novo corte.'); }
  }, [accept, client, jobId]);

  const submitStyle = useCallback(async (style: StyleSetup) => {
    setStyleSubmitting(true);
    try {
      awaitingVisual.current = true;
      await accept(await client.applyStyle(jobId, style));
      toast.success(hasVisual ? 'Refazendo o visual com o estilo novo.' : 'Gerando o visual.');
    } catch (e: any) {
      awaitingVisual.current = false;
      toast.error(e?.message || 'Erro ao enviar o estilo.');
    } finally { setStyleSubmitting(false); }
  }, [accept, client, jobId, hasVisual]);

  const saveVisual = useCallback(async (props: VisualProps, baseRevision: number): Promise<VisualSaveResult> => {
    setSaveState('saving');
    try {
      await accept(await client.saveVisual(jobId, props, baseRevision));
      setSaveState('saved');
      toast.success('Salvo — renderizando o MP4 final.');
      return 'ok';
    } catch (e: any) {
      setSaveState('error');
      if (isConflict(e)) { await reload(); return 'conflict'; }
      toast.error(e?.message || 'Erro ao salvar o visual.');
      return 'error';
    }
  }, [accept, client, jobId, reload]);

  const applyNotes = useCallback(async () => {
    const open = notes.all.filter(n => n.status === 'open' && n.text.trim());
    if (!open.length) return;
    setApplyingNotes(true);
    try {
      await accept(await client.applyNotes(jobId, open));
      notes.clearLocal();
      toast.success(`${open.length} ${open.length === 1 ? 'marcação enviada' : 'marcações enviadas'} — a IA vai refazer os trechos.`);
    } catch (e: any) { toast.error(e?.message || 'Erro ao aplicar marcações.'); } finally { setApplyingNotes(false); }
  }, [accept, client, jobId, notes]);

  const close = useCallback(() => {
    if (unsaved > 0 && !window.confirm('Existem ajustes não salvos. Fechar mesmo assim?')) return;
    onClose();
  }, [unsaved, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return;
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) { e.preventDefault(); setHelp(h => !h); }
      else if (e.key === 'Escape' && help) setHelp(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [help]);

  const editingNote = notes.editing ? notes.all.find(n => n.id === notes.editing!.id) : null;
  const projectName = title || job?.input?.originalFilenames?.[0] || 'Edição';
  const status = edvid?.error ? edvid.error : edvid?.message || (edvid ? PHASE_LABEL[edvid.phase] : '');

  const tabs = useMemo(() => [
    { id: 'cut' as const, label: '1 · Corte', enabled: true },
    { id: 'style' as const, label: 'Estilo', enabled: hasCut },
    { id: 'final' as const, label: '2 · Final', enabled: hasVisual },
  ], [hasCut, hasVisual]);

  const renderBusy = edvid?.phase === 'cutting' || edvid?.phase === 'visual_rendering';
  const publishBtn = (variant: 'primary' | 'secondary') => job && (
    <PublishButton job={job} client={client} disabled={renderBusy} variant={variant} onPublished={() => void reload()} />
  );
  const musicPatch = async (patch: { volume?: number; enabled?: boolean }) => {
    try { await client.updateMusic(jobId, patch); await reload(); } catch (e: any) { toast.error(e?.message || 'Não foi possível ajustar a trilha.'); }
  };

  let body: React.ReactNode;
  if (loading && !job) {
    body = <div className="flex flex-1 items-center justify-center text-sm font-bold text-slate-400">Abrindo edição…</div>;
  } else if (error && !job) {
    body = (
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="max-w-md rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 text-center">
          <p className="text-lg font-black text-white">Não foi possível abrir o editor</p>
          <p className="mt-2 text-sm text-rose-200">{error}</p>
          <button type="button" onClick={() => void reload()} className="mt-4 rounded-xl bg-white px-4 py-2 text-sm font-bold text-slate-900">Tentar novamente</button>
        </div>
      </div>
    );
  } else if (!edvid || !edvid.cut) {
    body = (
      <div className="relative flex flex-1 items-center justify-center">
        {edvid?.phase === 'failed'
          ? <div className="max-w-md rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 text-center"><p className="font-black text-white">O corte falhou</p><p className="mt-2 text-sm text-rose-200">{edvid.error || 'Erro técnico no processamento.'}</p></div>
          : <div className="relative h-64 w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-slate-950/70"><BusyOverlay title="Aguardando o primeiro corte" message={edvid?.message || 'Assim que o cut.mp4 existir, a timeline aparece aqui sozinha.'} /></div>}
      </div>
    );
  } else if (activeTab === 'cut') {
    body = (
      <CutTab
        edvid={edvid}
        busy={edvid.phase === 'cutting'}
        notes={notes}
        onSaveCut={saveCut}
        onRecut={recut}
        onApprove={() => setTab('style')}
        onDirtyChange={setCutDirty}
        onApplyNotes={() => void applyNotes()}
        applyingNotes={applyingNotes}
        toast={flash}
        publish={publishBtn('secondary')}
      />
    );
  } else if (activeTab === 'style') {
    body = (
      <StyleTab
        edvid={edvid}
        jobId={jobId}
        client={client}
        brandAccent={brandAccent}
        submitting={styleSubmitting}
        onSubmit={s => void submitStyle(s)}
        musicSelection={(job?.output as any)?.musicSelection || null}
        onMusicPatch={musicPatch}
      />
    );
  } else {
    body = (
      <FinalTab
        edvid={edvid}
        notes={notes}
        onSave={saveVisual}
        onRestyle={s => { void submitStyle(s); }}
        restyling={styleSubmitting}
        onDirtyChange={setFinalDirty}
        onApplyNotes={() => void applyNotes()}
        applyingNotes={applyingNotes}
        toast={flash}
        publish={publishBtn('primary')}
      />
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[2147482000] isolate flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-[#070b14] font-sans text-slate-100" data-testid="edvid-editor">
      <style>{`.edvid-scroll::-webkit-scrollbar{height:10px;width:10px}.edvid-scroll::-webkit-scrollbar-thumb{background:rgba(255,255,255,.12);border-radius:8px}.edvid-scroll::-webkit-scrollbar-track{background:transparent}`}</style>
      <header className="flex min-h-14 shrink-0 items-center gap-3 overflow-x-auto border-b border-white/10 bg-slate-950/80 px-3 py-2">
        <button type="button" onClick={close} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 hover:bg-white/10" aria-label="Fechar editor" title="Fechar"><IconBack /></button>
        <div className="min-w-0 shrink">
          <p className="max-w-[28vw] truncate text-sm font-black text-white">{projectName}</p>
          <p className={`max-w-[28vw] truncate text-[10px] ${edvid?.phase === 'failed' ? 'text-rose-300' : 'text-slate-500'}`}>
            {status}{edvid?.cut ? ` · corte rev. ${edvid.cut.revision}` : ''}{edvid?.visual ? ` · visual rev. ${edvid.visual.revision}` : ''}
          </p>
        </div>
        <nav className="mx-auto flex shrink-0 items-center gap-1 rounded-2xl border border-white/10 bg-slate-950/70 p-1" aria-label="Fases">
          {tabs.map(t => (
            <button
              key={t.id}
              type="button"
              disabled={!t.enabled}
              onClick={() => setTab(t.id)}
              aria-current={activeTab === t.id ? 'page' : undefined}
              className={`rounded-xl px-4 py-1.5 text-xs font-black transition-colors ${activeTab === t.id ? 'bg-indigo-500 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'} disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent`}
              title={!t.enabled ? (t.id === 'style' ? 'Disponível quando o corte existir' : 'Disponível depois de gerar o visual') : undefined}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <div className="flex shrink-0 items-center gap-2">
          {(edvid?.phase === 'cutting' || edvid?.phase === 'visual_rendering') && (
            <span className="flex items-center gap-1.5 rounded-full bg-indigo-500/15 px-2.5 py-1 text-[11px] font-black text-indigo-200">
              <span className="h-2 w-2 animate-pulse rounded-full bg-indigo-400" />{PHASE_LABEL[edvid.phase]}
            </span>
          )}
          <span className={`text-[11px] font-bold ${unsaved ? 'text-amber-300' : saveState === 'error' ? 'text-rose-400' : saveState === 'saving' ? 'text-slate-300' : 'text-emerald-400'}`}>
            {unsaved ? `${unsaved} ${unsaved === 1 ? 'ajuste não salvo' : 'ajustes não salvos'}` : saveState === 'saving' ? 'Salvando…' : saveState === 'error' ? 'Erro ao salvar' : 'Salvo'}
          </span>
          <button type="button" onClick={() => setHelp(true)} className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 text-sm font-black text-slate-300 hover:bg-white/10" title="Atalhos e gestos (?)" aria-label="Atalhos e gestos">?</button>
        </div>
      </header>

      {body}

      {editingNote && (
        <NoteEditor
          key={editingNote.id}
          note={editingNote}
          isNew={notes.editing!.isNew}
          editable={notes.isLocal(editingNote.id)}
          onSave={text => { notes.updateText(editingNote.id, text); notes.setEditing(null); }}
          onDelete={() => { notes.remove(editingNote.id); notes.setEditing(null); }}
          onClose={() => {
            if (notes.isLocal(editingNote.id) && !editingNote.text.trim()) notes.remove(editingNote.id);
            notes.setEditing(null);
          }}
        />
      )}
      {help && <HelpModal onClose={() => setHelp(false)} />}
    </div>,
    document.body,
  );
}
